import { UploadCloud } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button } from "../components/ui/Button";
import { Switch } from "../components/ui/Switch";
import { useConfirm } from "../components/ui/ConfirmDialog";
import { ErrorState, Skeleton } from "../components/ui/Feedback";
import { Input, Select } from "../components/ui/Input";
import { Stepper, type Step } from "../components/ui/Stepper";
import { useToast } from "../components/ui/Toast";
import { LabelWithHint } from "../components/ui/Tooltip";
import { ContentLibrary } from "../features/campaigns/ContentLibrary";
import { ScheduleField } from "../features/campaigns/ScheduleField";
import { ExperimentGroupField } from "../features/campaigns/ExperimentGroupField";
import { ThemeVisibilityField } from "../features/campaigns/ThemeVisibilityField";
import { PublishPrompt } from "../features/campaigns/PublishPrompt";
import { CodeEditor } from "../features/campaigns/CodeEditor";
import { SubscriptionEditor } from "../features/campaigns/SubscriptionEditor";
import { DiscountEditor } from "../features/campaigns/DiscountEditor";
import { RuleEditor } from "../features/campaigns/RuleEditor";
import { TierMessagesField } from "../features/campaigns/TierMessagesField";
import { AppearanceField } from "../features/campaigns/AppearanceField";
import { AudienceField } from "../features/campaigns/AudienceField";
import { appearanceOf, isAppearanceValid } from "../features/campaigns/appearance";
import { TierEditor } from "../features/campaigns/TierEditor";
import { useDiscountRules } from "../features/campaigns/useDiscountRules";
import { useOfferRules } from "../features/campaigns/useOfferRules";
import { useTiers } from "../features/campaigns/useTiers";
import { campaignTypeFor } from "../features/campaigns/campaignTypes";
import { CONTENT_TYPES } from "../features/campaigns/contentTypes";
import { useContentByPlacement } from "../features/campaigns/useContent";
import {
	useCampaign,
	useCampaigns,
	useDeleteCampaign,
	usePublishCampaign,
	useUpdateCampaign,
} from "../features/campaigns/useCampaigns";
import { useSettings, useShopTimeZone } from "../features/settings/useSettings";
import { celebrate } from "../lib/celebrate";
import { END_OF_DAY, scheduleToIso, toSchedule, type Schedule } from "../lib/dates";
import { formatRelative } from "../lib/format";
import { majorToMinor, minorToMajor } from "../lib/money";
import type { CampaignPatch, CampaignStatus } from "../lib/types";

const STATUSES: CampaignStatus[] = ["draft", "active", "ended"];

const DEFAULT_ROUND_UP = "0.05";

const Field = ({
	label,
	hint,
	help,
	children,
}: {
	label: string;
	hint?: string;
	help?: string;
	children: ReactNode;
}) => (
	<label className="block space-y-1.5">
		{help ? (
			<LabelWithHint label={label}>{help}</LabelWithHint>
		) : (
			<span className="text-sm text-ink">{label}</span>
		)}
		{children}
		{hint ? <span className="block text-xs text-ink-muted">{hint}</span> : null}
	</label>
);

export const CampaignPage = () => {
	const { campaignId } = useParams();
	const query = useCampaign(campaignId);
	const update = useUpdateCampaign(campaignId ?? "");
	const remove = useDeleteCampaign();
	const publish = usePublishCampaign(campaignId ?? "");
	const tiers = useTiers(campaignId);
	const rules = useOfferRules(campaignId);
	const discounts = useDiscountRules(campaignId);
	const campaigns = useCampaigns();
	const content = useContentByPlacement(
		campaignId ?? "",
		CONTENT_TYPES.map((type) => type.placement),
	);
	const navigate = useNavigate();
	const toast = useToast();
	const confirm = useConfirm();
	const { timeZone, fromSettings } = useShopTimeZone();
	const shopSettings = useSettings();

	const [step, setStep] = useState(0);
	const [form, setForm] = useState<CampaignPatch>({});
	const [schedule, setSchedule] = useState<Schedule>({
		range: undefined,
		startHour: 0,
		endHour: END_OF_DAY,
	});
	const [roundUpEnabled, setRoundUpEnabled] = useState(false);
	const [roundUp, setRoundUp] = useState(DEFAULT_ROUND_UP);

	useEffect(() => {
		const campaign = query.data?.campaign;
		if (!campaign) return;
		setForm({
			title: campaign.title,
			status: campaign.status,
			stacking: campaign.stacking,
			combinesOrderDiscounts: campaign.combinesOrderDiscounts,
			combinesProductDiscounts: campaign.combinesProductDiscounts,
			combinesShippingDiscounts: campaign.combinesShippingDiscounts,
			themeVisibility: campaign.themeVisibility,
			themeIds: campaign.themeIds,
			experimentGroupId: campaign.experimentGroupId,
			audienceParam: campaign.audienceParam,
			audienceValues: campaign.audienceValues,
			...appearanceOf(campaign),
		});
		setRoundUpEnabled(campaign.roundUpMinor > 0);
		setRoundUp(campaign.roundUpMinor > 0 ? minorToMajor(campaign.roundUpMinor) : DEFAULT_ROUND_UP);
	}, [query.data]);

	// Its own effect because the shop's time zone can arrive after the campaign:
	// read first in the browser's zone, the hours would be wrong until reload.
	useEffect(() => {
		const campaign = query.data?.campaign;
		if (!campaign) return;
		setSchedule(toSchedule(campaign.startsAt, campaign.endsAt, timeZone));
	}, [query.data, timeZone]);

	if (query.isPending) {
		return (
			<div className="space-y-4">
				<Skeleton className="h-8 w-64" />
				<Skeleton className="h-64 w-full" />
			</div>
		);
	}

	if (query.isError || !query.data) {
		return (
			<ErrorState
				title="Could not load campaign"
				description={(query.error as Error | null)?.message}
				action={
					<Link to="/campaigns">
						<Button variant="outline" size="sm">
							Back to campaigns
						</Button>
					</Link>
				}
			/>
		);
	}

	const save = (after?: () => void) => {
		if (!isAppearanceValid(appearanceOf(form))) {
			toast.push("Finish the colour you are typing: a hex value like #c8102e", "error");
			return;
		}
		update.mutate(
			{
				...form,
				...scheduleToIso(schedule, timeZone),
				roundUpMinor: roundUpEnabled ? majorToMinor(roundUp || "0") : 0,
			},
			{
				onSuccess: () => {
					toast.push("Saved");
					after?.();
				},
				onError: (error) => toast.push(error.message, "error"),
			},
		);
	};

	const destroy = () => {
		if (!campaignId) return;
		confirm({
			title: `Delete “${query.data.campaign.title}”?`,
			description: "Every tier, gift, and product configuration goes with it.",
			onConfirm: () =>
				remove.mutate(campaignId, {
					onSuccess: () => {
						toast.push("Campaign deleted");
						void navigate("/campaigns");
					},
					onError: (error) => toast.push(error.message, "error"),
				}),
		});
	};

	const campaign = query.data.campaign;
	const tierList = tiers.data?.items ?? [];
	const otherActiveCampaigns = (campaigns.data?.items ?? []).filter(
		(entry) => entry.status === "active" && entry.id !== campaign.id,
	).length;
	const pricedInCart = shopSettings.data?.settings.checkoutPricing === "cart_transform";
	const definition = campaignTypeFor(campaign.type);
	const singleTier = definition?.singleTier ?? false;
	const usesRules = definition?.builder === "rules";
	const usesDiscounts = definition?.builder === "discounts";
	const usesSubscriptions = definition?.builder === "subscriptions";
	const usesCode = definition?.builder === "code";
	const settings = definition?.settings ?? {
		stacking: true,
		roundUp: true,
		combinesWith: true,
	};

	const steps: readonly Step[] = [
		{
			id: "details",
			label: "Details",
			description: "Name and status",
			complete: campaign.title.trim().length > 0,
		},
		{
			id: "schedule",
			label: "Schedule",
			description: "When and where it runs",
			complete: Boolean(campaign.startsAt && campaign.endsAt),
		},
		{
			id: "rules",
			label: "Rules",
			description: settings.stacking ? "Stacking and round up" : "Discounts and rounding",
			complete: true,
		},
		{
			id: "tiers",
			label: definition?.rewardStep.label ?? "Tiers & gifts",
			description: definition?.rewardStep.description ?? "What shoppers earn",
			complete: usesCode
				? tierList.length > 0 &&
					tierList.every((tier) => tier.code !== null && tier.thresholdMinor > 0)
				: usesDiscounts
					? discounts.data !== undefined && discounts.data.items.length > 0
					: usesRules
						? rules.data !== undefined && rules.data.items.length > 0
						: tierList.length > 0 && tierList.every((tier) => tier.gifts.length > 0),
		},
		{
			id: "content",
			label: "Content",
			description: "Colours, popups and tickers",
			complete: content.some((query) => (query.data?.items.length ?? 0) > 0),
		},
	];

	const isLast = step === steps.length - 1;

	const publishNow = () =>
		publish.mutate(undefined, {
			onSuccess: (data) => {
				celebrate();
				toast.push(
					data.pricing === "discount" && data.discount === "removed"
						? "Published. Discount removed, the campaign is not active."
						: "Published",
				);
			},
			onError: (error) => toast.push(error.message, "error"),
		});

	return (
		<div className="space-y-6">
			<div className="space-y-2">
				<Link to="/campaigns" className="text-xs text-ink-muted underline-offset-4 hover:underline">
					← Campaigns
				</Link>
				<div className="flex flex-wrap items-center justify-between gap-3">
					<div>
						<h2 className="text-xl font-semibold tracking-tight text-ink">{campaign.title}</h2>
						<p className="text-sm text-ink-muted">
							{definition?.label ?? campaign.type}
							{campaign.publishedAt ? (
								<span> · published {formatRelative(campaign.publishedAt)}</span>
							) : (
								<span className="font-medium text-alert"> · not published</span>
							)}
						</p>
					</div>
					<Button onClick={publishNow} disabled={publish.isPending}>
						<UploadCloud className="h-4 w-4" strokeWidth={2} />
						{publish.isPending ? "Publishing…" : "Publish"}
					</Button>
				</div>
			</div>

			<PublishPrompt campaign={campaign} publishing={publish.isPending} onPublish={publishNow} />

			<div className="grid gap-8 lg:grid-cols-[15rem_1fr]">
				<Stepper steps={steps} current={step} onSelect={setStep} />

				<div className="min-w-0 space-y-6">
					{step === 0 ? (
						<div className="max-w-md space-y-5">
							<Field label="Name" help="Internal only. Shoppers never see this.">
								<Input
									value={form.title ?? ""}
									onChange={(event) => setForm({ ...form, title: event.target.value })}
								/>
							</Field>
							<Field
								label="Status"
								help="Draft is a work in progress. Active means the campaign is meant to be running. Ended stops it without losing the configuration."
							>
								<Select
									value={form.status ?? "draft"}
									onChange={(event) =>
										setForm({ ...form, status: event.target.value as CampaignStatus })
									}
								>
									{STATUSES.map((status) => (
										<option key={status} value={status}>
											{status.charAt(0).toUpperCase() + status.slice(1)}
										</option>
									))}
								</Select>
							</Field>
						</div>
					) : null}

					{step === 1 ? (
						<div className="space-y-5">
							<div className="space-y-1.5">
								<LabelWithHint label="Campaign dates">
									The window the offer runs for. Pick a start day, then an end day, on the calendar,
									then the hour it starts and ends.
								</LabelWithHint>
								<ScheduleField
									value={schedule}
									onChange={setSchedule}
									timeZone={timeZone}
									fromSettings={fromSettings}
								/>
							</div>
							<ThemeVisibilityField
								visibility={form.themeVisibility ?? "all_themes"}
								themeIds={form.themeIds ?? []}
								onChange={(next) => setForm({ ...form, ...next })}
							/>
							<ExperimentGroupField
								value={form.experimentGroupId ?? null}
								onChange={(experimentGroupId) => setForm({ ...form, experimentGroupId })}
							/>
							<AudienceField
								param={form.audienceParam ?? null}
								values={form.audienceValues ?? []}
								onChange={(next) => setForm({ ...form, ...next })}
							/>
						</div>
					) : null}

					{step === 2 ? (
						<div className="max-w-md space-y-6">
							{settings.stacking ? (
								<Field
									label="Stacking"
									help="“All cleared tiers” means a $200 cart gets the $30, $70, and $150 gifts together. “Highest tier only” gives just the $150 one."
								>
									<Select
										value={form.stacking ?? "all"}
										onChange={(event) =>
											setForm({ ...form, stacking: event.target.value as "all" | "highest" })
										}
									>
										<option value="all">All cleared tiers</option>
										<option value="highest">Highest tier only</option>
									</Select>
								</Field>
							) : null}

							{pricedInCart ? (
								<div className="space-y-1">
									<p className="text-sm font-medium text-ink">Combines with</p>
									<p className="text-xs text-ink-muted">
										Your store prices gifts in the cart, so every discount code stacks with this
										campaign. Change that under{" "}
										<Link to="/settings" className="underline underline-offset-4">
											Settings
										</Link>
										.
									</p>
								</div>
							) : (
								<div className="space-y-3">
									<LabelWithHint label="Combines with">
										Whether this campaign's gift discounts can stack with other discounts on the
										same order. Turn these off and a shopper using a promo code loses the gift
										discount; turn them on and both apply.
									</LabelWithHint>
									<Switch
										checked={form.combinesOrderDiscounts ?? false}
										onChange={(event) =>
											setForm({ ...form, combinesOrderDiscounts: event.target.checked })
										}
										label="Order discounts"
									/>
									<Switch
										checked={form.combinesProductDiscounts ?? false}
										onChange={(event) =>
											setForm({ ...form, combinesProductDiscounts: event.target.checked })
										}
										label="Other product discounts"
										hint="Includes your other campaigns. Leave on unless you deliberately want only one reward per order."
									/>
									{form.combinesProductDiscounts === false && otherActiveCampaigns > 0 ? (
										<p className="rounded-md border border-line-strong bg-raised px-3 py-2 text-xs text-ink-muted">
											You have {otherActiveCampaigns} other active campaign
											{otherActiveCampaigns === 1 ? "" : "s"}. With this off, Shopify applies only
											one of them per order and shoppers will not receive every reward.
										</p>
									) : null}
									<Switch
										checked={form.combinesShippingDiscounts ?? false}
										onChange={(event) =>
											setForm({ ...form, combinesShippingDiscounts: event.target.checked })
										}
										label="Shipping discounts"
									/>
								</div>
							)}

							{settings.roundUp ? (
								<div className="space-y-3">
									<Switch
										checked={roundUpEnabled}
										onChange={(event) => setRoundUpEnabled(event.target.checked)}
										label={singleTier ? "Round up" : "Round up to the next tier"}
										hint={
											singleTier
												? "A cart just short of the minimum spend still qualifies. Enter 0.05 and a $69.96 cart clears a $70 minimum."
												: "A cart just short of a threshold still unlocks it. Enter 0.05 and a $69.96 cart clears a $70 tier."
										}
									/>
									{roundUpEnabled ? (
										<Input
											type="number"
											min="0"
											step="0.01"
											value={roundUp}
											onChange={(event) => setRoundUp(event.target.value)}
											aria-label="Round up amount"
											className="ml-6 max-w-32"
										/>
									) : null}
								</div>
							) : null}
						</div>
					) : null}

					{step === 3 && campaignId ? (
						usesCode ? (
							<CodeEditor campaignId={campaignId} />
						) : usesSubscriptions ? (
							<SubscriptionEditor campaignId={campaignId} />
						) : usesDiscounts ? (
							<DiscountEditor campaignId={campaignId} />
						) : usesRules ? (
							<RuleEditor campaignId={campaignId} />
						) : (
							<div className="space-y-6">
								<TierMessagesField campaign={campaign} />
								<TierEditor campaignId={campaignId} singleTier={singleTier} />
							</div>
						)
					) : null}

					{step === 4 && campaignId ? (
						<div className="space-y-6">
							<AppearanceField
								value={appearanceOf(form)}
								onChange={(next) => setForm({ ...form, ...next })}
								showGiftPopup={!usesCode && !usesSubscriptions && !usesDiscounts && !usesRules}
							/>
							<ContentLibrary
								campaignId={campaignId}
								campaignType={campaign.type}
								campaignStartsAt={campaign.startsAt}
								campaignEndsAt={campaign.endsAt}
							/>
						</div>
					) : null}

					<div className="flex items-center justify-between border-t border-line pt-5">
						<Button
							variant="outline"
							disabled={step === 0}
							onClick={() => setStep((current) => current - 1)}
						>
							Back
						</Button>
						<div className="flex gap-2">
							<Button variant="ghost" onClick={destroy} disabled={remove.isPending}>
								Delete
							</Button>
							<Button
								onClick={() => save(isLast ? undefined : () => setStep((current) => current + 1))}
								disabled={update.isPending}
							>
								{update.isPending ? "Saving…" : isLast ? "Save" : "Save and continue"}
							</Button>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};
